import { Stack, Tile, Typography, useTheme } from "@carrotfertility/carotene-core"
import { ButtonSkeleton, Skeleton } from "@internal/ui"
import React from "react"
import { FormattedMessage, useIntl } from "react-intl"

/**
 * The gradient plan card, with the title every state shares.
 *
 * The heading is outside the branch on purpose: loading, failing and having no plan are all
 * answers to "what is my Carrot Plan", so the card keeps its identity instead of the page
 * appearing and disappearing under the member.
 */
export function PlanCard({ children }: { children: React.ReactNode }): JSX.Element {
  const theme = useTheme()
  // The design draws 36px by 38px padding and a 36px gap; no carotene token is either, and 36 is
  // equidistant from `xl` (32px) and `xxl` (40px). The tie is broken toward the design's frame:
  // the padding rounds UP so the card keeps the inset it was drawn with, and the gap rounds DOWN so
  // the two roundings do not compound into a card taller than the frame they were measured in.
  return (
    <Tile
      variant="primary"
      color="primary"
      // Redacted from Heap, which autocaptures the clicked text and a clicked link's href: which steps
      // a member has, and where they lead, is derived from their intake answers. On the card itself,
      // not the step list, so a click on its padding is covered too.
      data-heap-redact-text
      data-heap-redact-attributes="href"
      sx={(theme) => ({
        // The `sx` padding is what beats carotene's `Tile` theme default of `lg`.
        padding: theme.spacing(theme.tokens.spacing.xxl),
        // Below carotene's `sm` the 66/33 split has collapsed and the card is full-bleed, where 40px
        // each side takes a quarter of a 320px viewport. The nested step tiles keep their own `lg`,
        // which this matches rather than indenting them 32px narrower than the card they sit in.
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(theme.tokens.spacing.lg) }
      })}
    >
      <Stack spacing={theme.spacing(theme.tokens.spacing.xl)}>
        {/* `h3` for the design's 26px, `component="h2"` to keep the page's heading outline, and an
            explicit colour because carotene's `h3` is `text.secondary` while the design draws
            `text.primary`. The size is re-pinned below `sm`, where carotene shrinks `h3` to 23px —
            the design draws 26px at every width, and `h2` is no way out of it: carotene shrinks
            that one to 26px only below `sm` and draws 30px above. */}
        <Typography
          variant="h3"
          component="h2"
          color="text.primary"
          sx={(theme) => ({ [theme.breakpoints.down("sm")]: { fontSize: theme.typography.h3.fontSize } })}
        >
          <FormattedMessage defaultMessage="Your Carrot Plan" />
        </Typography>
        {children}
      </Stack>
    </Tile>
  )
}

// The commonest stack's length — 17 of the 34 have four steps — so most cold loads do not shift
// when the real card replaces this one.
const SKELETON_STEP_COUNT = 4

const SKELETON_TITLE_WIDTHS = ["62%", "48%", "70%", "54%"]

function PlanStepTileSkeleton({ titleWidth }: { titleWidth: string }): JSX.Element {
  const theme = useTheme()
  return (
    <Tile
      sx={(theme) => ({
        backgroundColor: theme.palette.background.paper,
        border: `${theme.tokens.borderWidth.sm} solid ${theme.palette.divider}`,
        boxShadow: "none"
      })}
    >
      <Stack spacing={theme.spacing(theme.tokens.spacing.xs)}>
        {/* 20px, the status chip's `small` icon, which stands taller than its 18px label. */}
        <Skeleton width={88} height={20} />
        <Skeleton width={titleWidth} height={24} />
        {/* 16px, because the descriptions these stand in for are `body1` now. */}
        <Skeleton width="100%" height={16} />
        <Skeleton width="76%" height={16} />
        <ButtonSkeleton width={132} sx={{ marginBlockStart: theme.spacing(theme.tokens.spacing.xs) }} />
      </Stack>
    </Tile>
  )
}

export function PlanCardSkeleton(): JSX.Element {
  const theme = useTheme()
  const intl = useIntl()
  return (
    <PlanCard>
      {/* `md` to match the settled step list: this frame is the host's `React.Suspense` fallback, so
          a gap of its own would shift the page on every cold load of the lazy plan chunk. */}
      <Stack
        role="status"
        aria-label={intl.formatMessage({ defaultMessage: "Loading your Carrot Plan" })}
        spacing={theme.spacing(theme.tokens.spacing.md)}
      >
        {Array.from({ length: SKELETON_STEP_COUNT }, (_unused, index) => (
          <PlanStepTileSkeleton key={index} titleWidth={SKELETON_TITLE_WIDTHS[index % SKELETON_TITLE_WIDTHS.length]} />
        ))}
      </Stack>
    </PlanCard>
  )
}
